import type { PathPoint } from '../data/map';

export interface PathCache {
  points: PathPoint[];
  cumulative: number[];
  total: number;
}

export function buildPathCache(points: PathPoint[]): PathCache {
  const cumulative: number[] = [0];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    total += Math.hypot(dx, dy);
    cumulative.push(total);
  }
  return { points, cumulative, total };
}

export function samplePath(cache: PathCache, dist: number): { x: number; y: number; angle: number } {
  const d = Math.max(0, Math.min(cache.total, dist));
  const pts = cache.points;
  const cum = cache.cumulative;
  // Binary search segment
  let lo = 0;
  let hi = cum.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (cum[mid] <= d) lo = mid;
    else hi = mid;
  }
  const a = pts[lo];
  const b = pts[lo + 1] ?? pts[lo];
  const segLen = cum[lo + 1] - cum[lo] || 1;
  const t = (d - cum[lo]) / segLen;
  const x = a.x + (b.x - a.x) * t;
  const y = a.y + (b.y - a.y) * t;
  const angle = Math.atan2(b.y - a.y, b.x - a.x);
  return { x, y, angle };
}

export function distanceToPathSegment(
  x: number,
  y: number,
  cache: PathCache,
): { dist: number; point: PathPoint; angle: number } {
  let best = Infinity;
  let bestPt = cache.points[0];
  let bestAngle = 0;
  const pts = cache.points;
  for (let i = 0; i < pts.length - 1; i++) {
    const ax = pts[i].x;
    const ay = pts[i].y;
    const bx = pts[i + 1].x;
    const by = pts[i + 1].y;
    const abx = bx - ax;
    const aby = by - ay;
    const apx = x - ax;
    const apy = y - ay;
    const ab2 = abx * abx + aby * aby || 1;
    let t = (apx * abx + apy * aby) / ab2;
    t = Math.max(0, Math.min(1, t));
    const px = ax + abx * t;
    const py = ay + aby * t;
    const dist = Math.hypot(x - px, y - py);
    if (dist < best) {
      best = dist;
      bestPt = { x: px, y: py };
      bestAngle = Math.atan2(aby, abx);
    }
  }
  return { dist: best, point: bestPt, angle: bestAngle };
}
